<template>
  <div class="wrapper" :style="cssStyles">
    <audio ref="audio" :src="src" preload="metadata" />
    <div class="player">
      <div class="toggle" @click="togglePlay">
        <svg-icon
          width="24"
          height="24"
          :icon="playState ? 'app-icons/pause' : 'app-icons/play'"
        />
      </div>
      <div class="current-time time info">{{ playtime }}</div>
      <div class="seek-slider">
        <input
          id="seek-slider"
          ref="seekSlider"
          v-model="seekValue"
          type="range"
          :max="seekMax"
          @input="onSeekInput"
          @change="onSeekChange"
        />
      </div>
      <div class="audio-duration time info">{{ duration }}</div>
    </div>
    <div v-if="volumeControl" class="volume">
      <div class="toggle" @click="toggleMute">
        <svg-icon
          :class="volumeClass"
          width="24"
          height="24"
          icon="app-icons/volume"
        />
      </div>
      <div class="current-volume info">
        {{ Math.round(volumeValue * 100) }}%
      </div>
      <div class="volume-slider">
        <input
          id="volume-slider"
          ref="volumeSlider"
          v-model="volumeValue"
          type="range"
          max="1"
          step="0.01"
          @input="onVolumeInput"
          @change="onVolumeChange"
        />
      </div>
      <div class="info" style="display:block" />
    </div>
  </div>
</template>

<script>
import SvgIcon from './SvgIcon.vue';

export default {
  components: {
    SvgIcon,
  },

  props: {
    src: {
      type: String,
      default: '',
    },

    volumeControl: {
      type: Boolean,
      default: true,
    },

    volume: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      playState: false,
      seconds: 0,
      audioDuration: 0,
      seekBeforeWidth: 0,
      volumeBeforeWidth: 100,
      seekMax: 100,
      bufferedAmmount: 0,
      seekValue: 0,
      volumeValue: 1,
      prevVolume: 1,
      raf: null,
    };
  },

  computed: {
    /**
     * Returns the current play time in 'MM:SS' format
     */
    playtime() {
      return this.secondsToMinutes(this.seconds);
    },

    /**
     * Returns the asset duration in 'MM:SS' format
     */
    duration() {
      return this.secondsToMinutes(this.audioDuration);
    },

    /**
     * Returns the buffered width of the asset
     */
    bufferedWidth() {
      return (this.bufferedAmmount / this.seekMax) * 100;
    },

    /**
     * Returns the CSS vars needed for the slider
     */
    cssStyles() {
      return {
        '--seek-before-width': `${this.seekBeforeWidth}%`,
        '--volume-before-width': `${this.volumeBeforeWidth}%`,
        '--buffered-width': `${this.bufferedWidth}%`,
      };
    },

    /**
     * Returns the correct class depending on the volume value
     */
    volumeClass() {
      const val = parseFloat(this.volumeValue);
      if (val > 0.75) {
        return 'full';
      }
      if (val > 0.5) {
        return 'third';
      }
      if (val > 0.25) {
        return 'second';
      }
      if (val > 0) {
        return 'first';
      }

      return 'muted';
    },
  },

  mounted() {
    this.volumeValue = this.volume;
    this.prevVolume = this.volumeValue;
    this.volumeBeforeWidth = Math.round(this.volumeValue * 100);

    if (this.$refs.audio.readyState > 0) {
      this.audioDuration = this.$refs.audio.duration;
      this.seekMax = Math.floor(this.$refs.audio.duration);
      if (this.$refs.audio.readyState === 4) {
        this.bufferedAmmount = 100;
      } else {
        this.bufferedAmmount = Math.floor(
          this.$refs.audio.buffered.end(this.$refs.audio.buffered.length - 1)
        );
      }
      this.$refs.audio.addEventListener('progress', () => {
        this.bufferedAmmount = Math.floor(
          this.$refs.audio.buffered.end(this.$refs.audio.buffered.length - 1)
        );
      });
    } else {
      this.$refs.audio.addEventListener('loadedmetadata', event => {
        this.audioDuration = this.$refs.audio.duration;
        this.seekMax = Math.floor(this.$refs.audio.duration);
        this.bufferedAmmount =
          this.$refs.audio.buffered.length > 0
            ? Math.floor(
                this.$refs.audio.buffered.end(
                  this.$refs.audio.buffered.length - 1
                )
              )
            : 0;
        this.$refs.audio.addEventListener('progress', () => {
          this.bufferedAmmount =
            this.$refs.audio.buffered.length > 0
              ? Math.floor(
                  this.$refs.audio.buffered.end(
                    this.$refs.audio.buffered.length - 1
                  )
                )
              : 0;
        });
      });
    }
    this.$refs.audio.addEventListener('ended', this.onEnded);
    this.$refs.audio.volume = this.volumeValue;
  },

  beforeUnmount() {
    this.$refs.audio.pause();
    this.$refs.audio.removeAttribute('src');
    this.$refs.audio.remove();
    this.playState = false;
    cancelAnimationFrame(this.raf);
  },

  methods: {
    togglePlay() {
      if (this.$refs.audio.ended) {
        this.seconds = 0;
        this.seekValue = 0;
      }
      this.playState = !this.playState;
      if (this.playState) {
        this.$refs.audio.play();
        requestAnimationFrame(this.whilePlaying);
      } else {
        this.$refs.audio.pause();
        cancelAnimationFrame(this.raf);
      }
    },

    onCloseModal() {
      this.setAudioAssetModal({ value: null });
    },

    /**
     * Transforms the integer value of seconds to a string 'MM:SS'
     * @param {Number} seconds  The number of seconds
     * @returns {String}        Time in 'MM:SS' format
     */
    secondsToMinutes(seconds) {
      seconds = Math.round(seconds);
      function strPadLeft(string, pad, length) {
        return (new Array(length + 1).join(pad) + string).slice(-length);
      }
      const minutes = Math.floor(seconds / 60);
      return `${minutes}:${strPadLeft(seconds % 60, '0', 2)}`;
    },

    onSeekInput(event) {
      this.seconds = Math.round(
        (event.target.value / event.target.max) * this.audioDuration
      );
      this.seekBeforeWidth = (event.target.value / event.target.max) * 100;
      if (!this.$refs.audio.paused) {
        cancelAnimationFrame(this.raf);
      }
      this.$emit('seekInput', { value: this.seconds });
    },

    onSeekChange(event) {
      this.$refs.audio.currentTime = event.target.value;
      if (!this.$refs.audio.paused) {
        requestAnimationFrame(this.whilePlaying);
      }
      this.$emit('seekChange', { value: this.seconds });
    },

    onVolumeInput(event) {
      const val = parseFloat(this.volumeValue);
      this.$refs.audio.volume = val;
      this.volumeBeforeWidth = val * 100;

      this.$emit('volumeInput', { value: val });
    },

    onVolumeChange(event) {
      this.volumeValue = parseFloat(this.volumeValue);
      if (this.volumeValue !== 0) {
        this.prevVolume = this.volumeValue;
      }
      this.$emit('volumeChange', { value: this.volumeValue });
    },

    toggleMute() {
      this.volumeValue = parseFloat(this.volumeValue);
      if (this.volumeValue === 0) {
        this.volumeValue = this.prevVolume;
      } else {
        this.prevVolume = this.volumeValue;
        this.volumeValue = 0;
      }

      this.$refs.audio.volume = this.volumeValue;
      this.volumeBeforeWidth = this.volumeValue * 100;
    },

    whilePlaying() {
      this.seekValue = Math.floor(this.$refs.audio.currentTime);
      this.seconds = this.seekValue;
      this.seekBeforeWidth = (this.seekValue / this.seekMax) * 100;
      this.raf = requestAnimationFrame(this.whilePlaying);
    },

    onEnded() {
      cancelAnimationFrame(this.raf);
      this.playState = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  .player,
  .volume {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 15px;
    padding: 30px;
    .toggle {
      cursor: pointer;
      &:hover svg path {
        fill: #fff;
      }
    }

    .info {
      width: 35px;
      text-align: center;
    }
  }

  .seek-slider,
  .volume-slider {
    flex: 1;
    display: flex;
  }
}

input[type='range'] {
  position: relative;
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
  padding: 0;
  height: 6px;
  float: left;
  outline: none;
  background-color: #242929;
  flex: 1;
}
input[type='range']::-webkit-slider-runnable-track {
  width: 100%;
  height: 4px;
  cursor: pointer;
  background: linear-gradient(
    to right,
    rgba(66, 75, 75, 0.842) var(--buffered-width),
    #242929 var(--buffered-width)
  );
}
input[type='range']::before {
  position: absolute;
  content: '';
  top: 2px;
  left: 0;
  width: var(--seek-before-width);
  height: 3px;
  background-color: #db841e;
  cursor: pointer;
}
input[type='range']::-webkit-slider-thumb {
  position: relative;
  appearance: none;
  -webkit-appearance: none;
  box-sizing: content-box;
  border: 1px solid #d9d9d9;
  height: 15px;
  width: 15px;
  border-radius: 50%;
  background-color: #d9d9d9;
  cursor: pointer;
  margin: -7px 0 0 0;
}
input[type='range']:active::-webkit-slider-thumb {
  transform: scale(1.2);
  background: #d9d9d9;
}
input[type='range']::-moz-range-track {
  width: 100%;
  height: 4px;
  cursor: pointer;
  background: linear-gradient(
    to right,
    rgba(66, 75, 75, 0.842) var(--buffered-width),
    #242929 var(--buffered-width)
  );
}
input[type='range']::-moz-range-progress {
  background-color: #d9d9d9;
}
input[type='range']::-moz-focus-outer {
  border: 0;
}
input[type='range']::-moz-range-thumb {
  box-sizing: content-box;
  border: 1px solid #d9d9d9;
  height: 15px;
  width: 15px;
  border-radius: 50%;
  background-color: #d9d9d9;
  cursor: pointer;
}
input[type='range']:active::-moz-range-thumb {
  transform: scale(1.2);
  background: #db841e;
}
input[type='range']::-ms-track {
  width: 100%;
  height: 3px;
  cursor: pointer;
  background: transparent;
  border: solid transparent;
  color: transparent;
}
input[type='range']::-ms-fill-lower {
  background-color: #db841e;
}
input[type='range']::-ms-fill-upper {
  background: linear-gradient(
    to right,
    rgba(66, 75, 75, 0.842) var(--buffered-width),
    #242929 var(--buffered-width)
  );
}
input[type='range']::-ms-thumb {
  box-sizing: content-box;
  height: 15px;
  width: 15px;
  border-radius: 50%;
  background-color: #d9d9d9;
  cursor: pointer;
}
input[type='range']:active::-ms-thumb {
  transform: scale(1.2);
  background: #d9d9d9;
}

#volume-slider::before {
  background: #d9d9d9;
}
#volume-slider::-ms-fill-lower {
  background: #d9d9d9;
}
#volume-slider:active::-moz-range-thumb {
  background: #d9d9d9;
}

#volume-slider::-webkit-slider-runnable-track {
  background: #242929;
}
#volume-slider::-moz-range-track {
  background: #242929;
}
#volume-slider::-ms-fill-upper {
  background: #242929;
}
#volume-slider::before {
  width: var(--volume-before-width);
}
</style>
<style lang="scss">
.wrapper .volume .toggle {
  svg path:nth-child(1) {
    opacity: 50%;
  }
  svg path:nth-child(2) {
    opacity: 70%;
  }
  svg path:nth-child(3) {
    opacity: 75%;
  }
  .muted {
    svg path {
      opacity: 25%;
    }
  }

  .first {
    svg path:nth-child(2),
    svg path:nth-child(3),
    svg path:nth-child(4) {
      opacity: 25%;
    }
  }

  .second {
    svg path:nth-child(3),
    svg path:nth-child(4) {
      opacity: 25%;
    }
  }

  .third {
    svg path:nth-child(4) {
      opacity: 25%;
    }
  }
}
</style>
